// Derin Theme - Standardized colors, spacing, and typography

// Cores
export const derinColors = {
  // Texto
  textPrimary: "#0A0A0A",
  textSecondary: "#717182",
  textComment: "#484848",
  textWhite: "#FFFFFF",
  textBlack: "#000000",

  // Backgrounds
  primaryDark: "#030213",
  backgroundLight: "#F3F3F5",
  backgroundWhite: "#FFFFFF",
  badgeBackground: "#ECEEF2",
  badgeInactive: "#DCDCDC",

  // Borders
  borderLight: "rgba(0, 0, 0, 0.1)",
  borderDark: "#000000",
  borderLeftAccent: "rgba(3, 2, 19, 0.3)",

  // Hover
  hoverBackground: "rgba(240, 237, 235, 0.3)",
  hoverButtonDark: "#1a1a2e",
  hoverButtonLight: "#F5F5F5",
};

// Color palette for feedbacks
const feedbackColorPalette = [
  { bg: "rgba(175, 156, 251, 0.5)", bgLight: "rgba(175, 156, 251, 0.1)" }, // purple
  { bg: "rgba(40, 178, 172, 0.5)", bgLight: "rgba(40, 178, 172, 0.1)" },   // teal
  { bg: "rgba(251, 156, 175, 0.5)", bgLight: "rgba(251, 156, 175, 0.1)" }, // pink
  { bg: "rgba(156, 204, 251, 0.5)", bgLight: "rgba(156, 204, 251, 0.1)" }, // blue
  { bg: "rgba(251, 207, 156, 0.5)", bgLight: "rgba(251, 207, 156, 0.1)" }, // orange
  { bg: "rgba(178, 223, 138, 0.5)", bgLight: "rgba(178, 223, 138, 0.1)" }, // green
];

const userColorCache: Record<string, { bg: string; bgLight: string }> = {};
let colorIndex = 0;

export const getColorForUser = (userName: string) => {
  if (!userColorCache[userName]) {
    userColorCache[userName] = feedbackColorPalette[colorIndex % feedbackColorPalette.length];
    colorIndex++;
  }
  return userColorCache[userName];
};

// Common spacing
export const derinSpacing = {
  cardPadding: "25px",
  cardBorderRadius: "14px",
  containerBorderRadius: "10px",
  buttonBorderRadius: "8px",
  buttonHeight: "36px",
  inputHeight: "36px",
  sectionGap: "30px",
  headerGap: "8px",
  feedbackGapSurvey: "20px",
  feedbackGapQuestion: "12px",
  questionsListGap: "10px",
};

// Tipografia comum
export const derinTypography = {
  heading: {
    fontWeight: 500,
    fontSize: "18px",
    lineHeight: "24px",
    letterSpacing: "-0.3125px",
  },
  body: {
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "-0.3125px",
  },
  bodyBold: {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "-0.3125px",
  },
  bodySemiBold: {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "-0.3125px",
  },
  badge: {
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
  },
  button: {
    fontWeight: 500,
    fontSize: "14px",
    letterSpacing: "-0.15px",
  },
  subtitle: {
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: "-0.15px",
  },
};

// Reusable Card styles
export const derinCardStyles = {
  container: {
    background: derinColors.backgroundWhite,
    border: `1px solid ${derinColors.borderLight}`,
    borderRadius: derinSpacing.cardBorderRadius,
    padding: derinSpacing.cardPadding,
    boxSizing: "border-box" as const,
  },
  questionContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    padding: "17px",
    gap: "12px",
    width: "100%",
    border: `1px solid ${derinColors.borderLight}`,
    borderRadius: derinSpacing.containerBorderRadius,
    boxSizing: "border-box" as const,
  },
};

// Reusable Input/Select styles
export const derinInputStyles = {
  select: {
    width: "192px",
    height: derinSpacing.inputHeight,
    background: derinColors.backgroundLight,
    borderRadius: derinSpacing.buttonBorderRadius,
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "-0.15px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-select": {
      py: 1,
      px: 1.5,
    },
    "& .MuiSelect-icon": {
      opacity: 0.5,
      right: 12,
    },
  },
  searchContainer: {
    width: "256px",
    height: derinSpacing.inputHeight,
    background: derinColors.backgroundLight,
    borderRadius: derinSpacing.buttonBorderRadius,
    display: "flex",
    alignItems: "center",
    pl: 1.5,
    pr: 1.5,
  },
  searchInput: {
    flex: 1,
    fontSize: "14px",
    fontWeight: 400,
    letterSpacing: "-0.15px",
    "& input::placeholder": {
      color: derinColors.textSecondary,
      opacity: 1,
    },
  },
};
